import React from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import {
  FaTimes,
  FaCalendarAlt,
  FaClock,
  FaMapMarkerAlt,
  FaVideo,
  FaUser,
  FaUserTie,
  FaExternalLinkAlt,
  FaEdit,
  FaTrashAlt,
  FaBell,
  FaPlaneDeparture,
  FaFileAlt,
  FaTasks,
  FaQuestionCircle,
  FaFileInvoiceDollar,
} from "react-icons/fa";

const CalendarEventDetailModal = ({ isOpen, onClose, event, onEdit, onDeleted }) => {
  if (!isOpen || !event) return null;

  const formatDate = (isoStr) => {
    if (!isoStr) return "N/A";
    const d = new Date(isoStr);
    if (isNaN(d.getTime())) return isoStr;
    return d.toLocaleString(undefined, {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getSourceIcon = (source) => {
    switch (source) {
      case "sales":
        return <FaPlaneDeparture style={{ color: "#0284c7" }} />;
      case "documents":
        return <FaFileAlt style={{ color: "#e11d48" }} />;
      case "tasks":
        return <FaTasks style={{ color: "#4f46e5" }} />;
      case "inquiries":
        return <FaQuestionCircle style={{ color: "#d97706" }} />;
      case "invoices":
        return <FaFileInvoiceDollar style={{ color: "#059669" }} />;
      default:
        return <FaCalendarAlt style={{ color: "#2563eb" }} />;
    }
  };

  const getSourceLink = () => {
    if (!event.module_source || event.module_source === "calendar") return null;

    let path = "#";
    let label = "View Linked Record";

    switch (event.module_source) {
      case "sales":
        path = event.source_id ? `/dashboard/sales/edit/${event.source_id}` : "/dashboard/sales/list";
        label = "View Flight / Sale in Sales Module";
        break;
      case "tasks":
        path = "/dashboard/tasks";
        label = "Open Task in Task Manager";
        break;
      case "inquiries":
        path = event.source_id ? `/dashboard/inquiries/${event.source_id}` : "/dashboard/inquiries/list";
        label = "Open Client Inquiry";
        break;
      case "documents":
        path = event.source_id ? `/dashboard/documents/${event.source_id}` : "/dashboard/documents/list";
        label = "Inspect Managed Document";
        break;
      case "invoices":
        path = "/dashboard/receipts/list";
        label = "View Invoices & Receipts";
        break;
      default:
        break;
    }

    return (
      <Link to={path} className="link-out-btn" onClick={onClose}>
        <FaExternalLinkAlt /> {label}
      </Link>
    );
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this scheduled calendar event?")) {
      return;
    }
    try {
      await axios.delete(`${API_BASE_URL}/calendar/events/${event.id}`);
      if (onDeleted) onDeleted();
      onClose();
    } catch (err) {
      alert("Failed to delete event.");
    }
  };

  const isManualEvent = event.module_source === "calendar";

  return (
    <div className="calendar-modal-overlay" onClick={onClose}>
      <div className="calendar-modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="calendar-modal-header">
          <h3>
            <FaCalendarAlt style={{ color: "#2563eb" }} />
            Event Details
          </h3>
          <button className="calendar-modal-close-btn" onClick={onClose} type="button">
            <FaTimes />
          </button>
        </div>

        <div className="calendar-modal-body">
          <div style={{ marginBottom: 18 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <span className="event-type-badge badge-meeting">
                {event.event_type}
              </span>
              {event.priority && (
                <span
                  style={{
                    fontSize: 11,
                    textTransform: "uppercase",
                    fontWeight: 700,
                    padding: "2px 8px",
                    borderRadius: 4,
                    backgroundColor:
                      event.priority === "urgent"
                        ? "#fee2e2"
                        : event.priority === "high"
                        ? "#ffedd5"
                        : "#f1f5f9",
                    color:
                      event.priority === "urgent"
                        ? "#dc2626"
                        : event.priority === "high"
                        ? "#c2410c"
                        : "#475569",
                  }}
                >
                  {event.priority}
                </span>
              )}
              <span className="detail-source-badge">
                {getSourceIcon(event.module_source)}
                {event.module_source ? `Module: ${event.module_source.toUpperCase()}` : "Manual Event"}
              </span>
            </div>

            <h2 style={{ fontSize: 20, fontWeight: 700, color: "#0f172a", margin: 0 }}>
              {event.title}
            </h2>
          </div>

          <div style={{ borderTop: "1px solid #f1f5f9", paddingTop: 16 }}>
            <div className="detail-meta-item">
              <div className="detail-meta-icon">
                <FaClock />
              </div>
              <div className="detail-meta-content">
                <div className="detail-meta-label">Schedule & Time</div>
                <div className="detail-meta-val">
                  {formatDate(event.start_time)}
                  {event.end_time && ` — ${formatDate(event.end_time)}`}
                  {event.is_all_day && " (All Day)"}
                </div>
              </div>
            </div>

            {event.client_name && (
              <div className="detail-meta-item">
                <div className="detail-meta-icon">
                  <FaUser />
                </div>
                <div className="detail-meta-content">
                  <div className="detail-meta-label">Client / Customer</div>
                  <div className="detail-meta-val">{event.client_name}</div>
                </div>
              </div>
            )}

            {event.assigned_to && (
              <div className="detail-meta-item">
                <div className="detail-meta-icon">
                  <FaUserTie />
                </div>
                <div className="detail-meta-content">
                  <div className="detail-meta-label">Assigned Representative</div>
                  <div className="detail-meta-val">{event.assigned_to}</div>
                </div>
              </div>
            )}

            {event.location && (
              <div className="detail-meta-item">
                <div className="detail-meta-icon">
                  <FaMapMarkerAlt />
                </div>
                <div className="detail-meta-content">
                  <div className="detail-meta-label">Location</div>
                  <div className="detail-meta-val">{event.location}</div>
                </div>
              </div>
            )}

            {event.meeting_link && (
              <div className="detail-meta-item">
                <div className="detail-meta-icon">
                  <FaVideo />
                </div>
                <div className="detail-meta-content">
                  <div className="detail-meta-label">Meeting URL</div>
                  <div className="detail-meta-val">
                    <a
                      href={event.meeting_link.startsWith("http") ? event.meeting_link : `https://${event.meeting_link}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#2563eb", textDecoration: "underline" }}
                    >
                      {event.meeting_link}
                    </a>
                  </div>
                </div>
              </div>
            )}

            {event.reminder_minutes_before !== undefined && event.reminder_minutes_before !== null && (
              <div className="detail-meta-item">
                <div className="detail-meta-icon">
                  <FaBell />
                </div>
                <div className="detail-meta-content">
                  <div className="detail-meta-label">Reminder Notice</div>
                  <div className="detail-meta-val">
                    {event.reminder_minutes_before === 0
                      ? "At time of event"
                      : `${event.reminder_minutes_before} minutes prior`}
                  </div>
                </div>
              </div>
            )}

            {event.description && (
              <div className="detail-meta-item">
                <div className="detail-meta-icon">
                  <FaFileAlt />
                </div>
                <div className="detail-meta-content">
                  <div className="detail-meta-label">Notes & Details</div>
                  <div
                    className="detail-meta-val"
                    style={{ whiteSpace: "pre-wrap", color: "#334155", marginTop: 4, lineHeight: 1.5 }}
                  >
                    {event.description}
                  </div>
                </div>
              </div>
            )}

            {getSourceLink() && (
              <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px dashed #e2e8f0" }}>
                {getSourceLink()}
              </div>
            )}
          </div>
        </div>

        <div className="calendar-modal-footer">
          {isManualEvent && (
            <>
              <button
                type="button"
                className="danger-btn"
                onClick={handleDelete}
              >
                <FaTrashAlt /> Delete
              </button>
              <button
                type="button"
                className="calendar-secondary-btn"
                onClick={() => {
                  onClose();
                  onEdit(event);
                }}
              >
                <FaEdit /> Edit Event
              </button>
            </>
          )}
          <button
            type="button"
            className="calendar-primary-btn"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CalendarEventDetailModal;
